import { bytesToBase64 } from '../../utils/base64';
import { substituteKeyId, type SigningKey } from '../kiosk-release-keys/releaseKeyCrypto';
import { ManifestRuleError, parseReleaseManifest, type ReleaseManifest } from './manifestRules';

export { bytesToBase64 };

export const SIGNATURE_LENGTH = 88;
const PLACEHOLDER_CHECK_KEY_ID = 'rk-000000000000000000000000';

export class ReleaseFileError extends Error {
  constructor(readonly reason: 'manifest_empty' | 'signature_format' | 'package_not_zip' | 'package_mismatch') {
    super(reason);
    this.name = 'ReleaseFileError';
  }
}

export class ReleaseUploadError extends Error {
  constructor(readonly status: number) {
    super(`upload failed with status ${status}`);
    this.name = 'ReleaseUploadError';
  }
}

export interface UnsignedSummary {
  kind: string;
  version: string;
  build: number;
  gitCommit: string;
  environmentPolicy: string[];
  octopusAffecting: boolean;
  journalSchema: number | null;
  journalBreaking: boolean;
  fileCount: number;
  packageSize: number;
  packageSha256: string;
}

function readFile<T>(file: Blob, read: (reader: FileReader) => void, take: (reader: FileReader) => T): Promise<T> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(take(reader));
    reader.onerror = () => reject(reader.error);
    read(reader);
  });
}

const fileBytes = (file: Blob) => readFile(file, (reader) => reader.readAsArrayBuffer(file), (reader) => new Uint8Array(reader.result as ArrayBuffer));
export const fileText = (file: Blob) => readFile(file, (reader) => reader.readAsText(file), (reader) => String(reader.result));

export async function readReleaseFiles(manifest: Blob, signature: Blob, packageFile: File): Promise<{ manifestB64: string; signatureB64: string }> {
  const manifestBytes = await fileBytes(manifest);
  if (manifestBytes.length === 0) throw new ReleaseFileError('manifest_empty');
  const signatureText = await fileText(signature);
  if (signatureText.length !== SIGNATURE_LENGTH || !/^[A-Za-z0-9+/]{86}==$/.test(signatureText)) throw new ReleaseFileError('signature_format');
  if (!packageFile.name.toLowerCase().endsWith('.zip')) throw new ReleaseFileError('package_not_zip');
  return { manifestB64: bytesToBase64(manifestBytes), signatureB64: signatureText };
}

export async function putPackage(uploadUrl: string, packageFile: Blob): Promise<void> {
  const response = await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': 'application/zip', 'x-upsert': 'false' }, body: packageFile });
  if (!response.ok) throw new ReleaseUploadError(response.status);
}

function summaryOf(manifest: ReleaseManifest): UnsignedSummary {
  return {
    kind: manifest.kind,
    version: manifest.version,
    build: manifest.build,
    gitCommit: manifest.git_commit,
    environmentPolicy: manifest.environment_policy,
    octopusAffecting: manifest.octopus_affecting,
    journalSchema: manifest.journal_schema,
    journalBreaking: manifest.journal_breaking,
    fileCount: manifest.files.length,
    packageSize: manifest.package.size,
    packageSha256: manifest.package.sha256,
  };
}

async function sha256Hex(file: Blob): Promise<string> {
  const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', await fileBytes(file)));
  return Array.from(digest, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

export async function readUnsignedRelease(manifest: Blob, packageFile: File): Promise<{ manifestBytes: Uint8Array; summary: UnsignedSummary }> {
  if (!packageFile.name.toLowerCase().endsWith('.zip')) throw new ReleaseFileError('package_not_zip');
  const manifestBytes = await fileBytes(manifest);
  if (manifestBytes.length === 0) throw new ReleaseFileError('manifest_empty');
  const summary = summaryOf(parseReleaseManifest(substituteKeyId(manifestBytes, PLACEHOLDER_CHECK_KEY_ID)));
  if (packageFile.size !== summary.packageSize || (await sha256Hex(packageFile)) !== summary.packageSha256) throw new ReleaseFileError('package_mismatch');
  return { manifestBytes, summary };
}

export async function signRelease(manifestBytes: Uint8Array, key: SigningKey): Promise<{ manifestB64: string; signatureB64: string; signedManifest: Uint8Array }> {
  const signedManifest = substituteKeyId(manifestBytes, key.keyId);
  if (parseReleaseManifest(signedManifest).key_id !== key.keyId) throw new ManifestRuleError('key_id');
  const signatureB64 = await key.sign(signedManifest);
  return { manifestB64: bytesToBase64(signedManifest), signatureB64, signedManifest };
}
